import React from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { CitizenParamList } from '../../../typings/Params';
import Filters from '../../../components/Filter';
import { useState } from 'react';
import moment from 'moment';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { translate } from '../../../services/translate';
import { View } from 'react-native';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import FilterInput from '../../../components/Filter/components/FilterInput';

interface Props {
    filters: CitizenParamList['CitizenLprSearch'];
    setFilters: React.Dispatch<React.SetStateAction<CitizenParamList['CitizenLprSearch']>>;
    plateFilter: string;
    setPlateFilter: React.Dispatch<React.SetStateAction<string>>;
}

export default function LprSearchFilter({ filters, setFilters, plateFilter, setPlateFilter }: Props) {
    const navigation = useNavigation<NavigationProp<CitizenParamList, 'CitizenLprSearch'>>();

    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [irregularSituationFilter, setIrregularSituationFilter] = useState(filters.irregularSituation ?? false);

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(6);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(5);

    const handleCleanFilter = () => {
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setIrregularSituationFilter(false);
        setPlateFilter('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            plate: plateFilter !== '' ? plateFilter.toUpperCase() : undefined,
            irregularSituation: irregularSituationFilter,
        });

        navigation.setParams({
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            plate: plateFilter !== '' ? plateFilter.toUpperCase() : undefined,
            irregularSituation: irregularSituationFilter,
        });
    };

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('plate')}
                value={plateFilter}
                placeholder={translate('typeToSearch')}
                onChange={setPlateFilter}
                maxCharacters={7}
                toUppercase={true}
            />
            <View style={{ zIndex: estimatedStartZIndex }}>
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('beginIn')}
                    zIndex={6}
                    onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                    onCalendarClose={() => setEstimatedStartZIndex(6)}
                />
            </View>
            <View style={{ zIndex: estimatedFinishZIndex }}>
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    zIndex={5}
                    onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                    onCalendarClose={() => setEstimatedFinishZIndex(5)}
                />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <FilterCheckBox
                    label={translate('irregularSituation')}
                    value={irregularSituationFilter}
                    onChange={setIrregularSituationFilter}
                />
            </View>
        </Filters>
    );
}
